import { message } from 'antd';
import axios from 'axios';
import type { Dayjs } from 'dayjs';
import { useMutation, useQuery, useQueryClient } from 'react-query';

export interface ExternalOrderListItem {
  id: number;
  user_id: number;
  serial: string;
  state: string;
  redeem_time: string;
  pickup_no: string;
  location_id: number;
  type: string;
  location_name: string;
  time: string;
  receiver_name: string;
  user_email: string | null;
  user_name: string | null;
  total_quantity: number | null;
  total_price: number | null;
  item_count: number | null;
}

export interface ExternalOrderItemListRow {
  item_id: number;
  order_id: number;
  serial: string;
  state: string;
  redeem_time: string;
  pickup_no: string;
  location_id: number;
  location_name: string;
  receiver_name: string;
  time: string;
  user_id: number;
  user_email: string | null;
  user_name: string | null;
  product_id: number;
  product_name: string;
  product_en_name: string | null;
  provider_id: number;
  provider_name: string;
  price: number;
  quantity: number;
  customer_comment: string | null;
}

export interface ExternalOrderItem {
  id: number;
  order_id: number;
  product_id: number;
  price: number;
  customer_comment: string | null;
  quantity: number;
  provider_id: number;
  product_name: string;
  provider_name: string;
  product_en_name: string | null;
}

export interface ExternalOrderTopping {
  order_item_id: number;
  topping_key_id: number;
  topping_value_id: number;
  topping_key: string;
  topping_value: string;
}

export interface ExternalOrderLocation {
  location_id: number;
  location_name: string;
}

export interface ProductStockRow {
  redeem_time: string;
  location_id: number;
  product_id: number;
  stock: number;
  sold: number;
  product_name: string | null;
  product_code: string | null;
  provider_id: number | null;
  provider_name: string | null;
  location_name: string | null;
}

export interface ExternalOrderProvider {
  provider_id: number;
  provider_name: string;
}

export type CancelExternalOrderReason = 'not_found' | 'already_cancelled';

export interface CancelExternalOrderResult {
  id: number;
  ok: boolean;
  reason?: CancelExternalOrderReason;
}

export interface FindExternalOrdersParams {
  skip?: number;
  take?: number;
  keyword?: string;
  startDate?: Dayjs;
  endDate?: Dayjs;
  locationIds?: number[];
  productName?: string;
  providerName?: string;
  providerId?: number;
}

export function useExternalOrders(params: FindExternalOrdersParams) {
  return useQuery(['external-orders', params], async () => {
    const response = await axios.get<{
      items: ExternalOrderItemListRow[];
      total: number;
    }>('/external-orders', {
      params: {
        skip: params.skip,
        take: params.take,
        keyword: params.keyword || undefined,
        startDate: params.startDate?.toISOString(),
        endDate: params.endDate?.toISOString(),
        locationIds: params.locationIds?.length
          ? params.locationIds
          : undefined,
        productName: params.productName || undefined,
        providerName: params.providerName || undefined,
        providerId: params.providerId || undefined,
      },
    });
    return response.data;
  });
}

export function useExternalOrder(id: number | null) {
  return useQuery(
    ['external-orders', 'detail', id],
    async () => {
      const response = await axios.get<{
        order: ExternalOrderListItem;
        items: ExternalOrderItem[];
        toppings: ExternalOrderTopping[];
      }>(`/external-orders/${id}`);
      return response.data;
    },
    { enabled: id != null },
  );
}

export function useExternalOrderLocations() {
  return useQuery(['external-orders', 'locations'], async () => {
    const response = await axios.get<ExternalOrderLocation[]>(
      '/external-orders/locations',
    );
    return response.data;
  });
}

export function useExternalOrderProviders() {
  return useQuery(['external-orders', 'providers'], async () => {
    const response = await axios.get<ExternalOrderProvider[]>(
      '/external-orders/providers',
    );
    return response.data;
  });
}

export function useCancelExternalOrders() {
  const queryClient = useQueryClient();
  return useMutation(
    ['external-orders', 'cancel'],
    async (ids: number[]) => {
      const response = await axios.patch<{
        results: CancelExternalOrderResult[];
      }>('/external-orders/cancel', { ids });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['external-orders']);
        queryClient.invalidateQueries(['external-product-stocks']);
      },
      onError: () => {
        message.error('取消訂單失敗');
      },
    },
  );
}

export function useProductStocks(params: FindExternalOrdersParams) {
  return useQuery(['external-product-stocks', params], async () => {
    const response = await axios.get<{
      items: ProductStockRow[];
      total: number;
    }>('/external-product-stocks', {
      params: {
        skip: params.skip,
        take: params.take,
        keyword: params.keyword || undefined,
        startDate: params.startDate?.toISOString(),
        endDate: params.endDate?.toISOString(),
        locationIds: params.locationIds?.length
          ? params.locationIds
          : undefined,
        productName: params.productName || undefined,
        providerName: params.providerName || undefined,
      },
    });
    return response.data;
  });
}

export function useUpdateProductStock() {
  const queryClient = useQueryClient();
  return useMutation(
    ['external-product-stocks', 'update'],
    (data: {
      redeemTime: string;
      locationId: number;
      productId: number;
      stock: number;
    }) => axios.patch('/external-product-stocks', data),
    {
      onSuccess: () => {
        message.success('庫存已更新');
        queryClient.invalidateQueries(['external-product-stocks']);
      },
      onError: () => {
        message.error('庫存更新失敗');
      },
    },
  );
}
